import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  Select,
  Tooltip,
} from '@mui/material';

import React from 'react';

import ClickAwayListener from './clickAwayListener';

const noop = () => {};

function CTRMetricPicker({
  anchorEl,
  onSelectViews = noop,
  onSelectClicks = noop,
  onClose = noop,
  currentClicks = [],
  clickItems = [],
  currentViews = ['page_view', 'screen_view'],
  viewItems = ['page_view', 'screen_view'],
}) {
  const popperRef = React.useRef(null);
  const selectViewsMenuRef = React.useRef(null);
  const selectClicksMenuRef = React.useRef(null);

  const [selectedViews, setSelectedViews] = React.useState(currentViews);
  const [selectedClicks, setSelectedClicks] = React.useState(currentClicks);

  React.useEffect(() => {
    setSelectedViews(currentViews);
  }, [currentViews]);

  React.useEffect(() => {
    setSelectedClicks(currentClicks);
  }, [currentClicks]);

  const handleChangeViews = (event) => {
    const {
      target: { value },
    } = event;

    if (value.length === 0) {
      return;
    }

    setSelectedViews(typeof value === 'string' ? value.split(',') : value);
  };

  const handleChangeClicks = (event) => {
    const {
      target: { value },
    } = event;

    if (value.length === 0) {
      return;
    }

    setSelectedClicks(typeof value === 'string' ? value.split(',') : value);
  };

  const handleApply = () => {
    onSelectViews(null, selectedViews);
    onSelectClicks(null, selectedClicks);
    onClose();
  };

  const handleCancel = () => {
    setSelectedViews(currentViews);
    setSelectedClicks(currentClicks);
    onClose();
  };

  return (
    <Popper
      id={anchorEl ? 'ctr-views-clicks-picker' : undefined}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement='bottom'
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'viewport',
            fallbackPlacements: [
              'top-start',
              'top',
              'top-end',
              'bottom-start',
              'bottom-end',
            ],
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: false,
            rootBoundary: 'viewport',
          },
        },
      ]}
      sx={{ zIndex: 10 }}
    >
      <ClickAwayListener
        safeRefs={[popperRef, selectClicksMenuRef, selectViewsMenuRef]}
        onClickAway={onClose}
      >
        <Paper
          ref={popperRef}
          elevation={6}
          sx={{
            mt: 1,
            maxHeight: 600,
            overflow: 'auto',
            maxWidth: 300,
          }}
        >
          <Box p={1}>
            <Box>
              <FormControl sx={{ m: 1, width: 250 }}>
                <InputLabel id='views-multiple-checkbox-label'>
                  <Tooltip
                    title={'Select views event related to CTR calculation'}
                    arrow
                  >
                    <Box
                      component='span'
                      sx={{
                        textDecoration: 'underline',
                        textDecorationStyle: 'dashed',
                        textDecorationThickness: '0.1rem',
                        textDecorationColor: 'inherit',
                      }}
                    >
                      View Events
                    </Box>
                  </Tooltip>
                </InputLabel>
                <Select
                  labelId='views-multiple-checkbox-label'
                  id='views-multiple-checkbox'
                  multiple
                  displayEmpty
                  value={selectedViews}
                  onChange={handleChangeViews}
                  input={<OutlinedInput label='View Events' />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <em>Placeholder</em>;
                    }
                    return selected.join(', ');
                  }}
                  MenuProps={{
                    PaperProps: { ref: selectViewsMenuRef },
                  }}
                >
                  <MenuItem disabled value=''>
                    <em>Select views</em>
                  </MenuItem>
                  {viewItems.map((view) => (
                    <MenuItem key={view} value={view}>
                      <Checkbox
                        disabled={
                          selectedViews.length === 1 &&
                          selectedViews.includes(view)
                        }
                        checked={selectedViews.indexOf(view) > -1}
                      />
                      <ListItemText primary={view} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <FormControl sx={{ m: 1, width: 250 }}>
                <InputLabel id='clicks-multiple-checkbox-label'>
                  <Tooltip
                    title={'Select click events related to CTR calculation'}
                    arrow
                  >
                    <Box
                      component='span'
                      sx={{
                        textDecoration: 'underline',
                        textDecorationStyle: 'dashed',
                        textDecorationThickness: '0.1rem',
                        textDecorationColor: 'inherit',
                      }}
                    >
                      Click Events
                    </Box>
                  </Tooltip>
                </InputLabel>

                <Select
                  labelId='clicks-multiple-checkbox-label'
                  id='clicks-multiple-checkbox'
                  multiple
                  displayEmpty
                  value={selectedClicks}
                  onChange={handleChangeClicks}
                  input={<OutlinedInput label='Click Events' />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <em>Placeholder</em>;
                    }
                    return selected.join(', ');
                  }}
                  MenuProps={{
                    PaperProps: { ref: selectClicksMenuRef },
                  }}
                >
                  <MenuItem disabled value=''>
                    <em>Select clicks</em>
                  </MenuItem>
                  {clickItems.map((click) => (
                    <MenuItem key={click} value={click}>
                      <Checkbox
                        disabled={
                          selectedClicks.length === 1 &&
                          selectedClicks.includes(click)
                        }
                        checked={selectedClicks.indexOf(click) > -1}
                      />
                      <ListItemText primary={click} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Action buttons section */}
            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, pt: 0 }}
            >
              <Button onClick={handleCancel} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button onClick={handleApply} variant='contained'>
                Apply
              </Button>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}

export default CTRMetricPicker;
